import { HttpClient } from '@angular/common/http';
import { ElementRef, Injectable, inject } from '@angular/core';
import {
    IIqTrendlinesGraphLtNtlStateService,
    LoadTruckResponse,
    Ratio,
    TL_NTL_LT_URL,
    TRENDLINES_CACHE_DB,
    TRENDLINES_NTL_FLATBED_LT_GRAPH_DOC,
    TRENDLINES_NTL_REEFER_LT_GRAPH_DOC,
    TRENDLINES_NTL_VAN_LT_GRAPH_DOC,
} from '../../models';

import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import { DatDbClientService } from '@dat/db';
import { lastValueFrom } from 'rxjs';
import * as FlatbedGraph from './graph-flatbed';
import * as ReeferGraph from './graph-reefer';
import * as VanGraph from './graph-van';
import { DatiqAppConfig } from '@dat/datiq-core/models';

type LtResponse = {
    code: string;
    loads: number;
    trucks: number;
    ratio: number;
    period: string;
};

type CachedLtData = {
    data: Ratio[];
    expiryDate: string;
};

type PouchDoc = {
    data: Ratio[];
    expiryDate: Date;
}

const LT_DOC = new Map<string, string>([
    ['Van', TRENDLINES_NTL_VAN_LT_GRAPH_DOC],
    ['Reefer', TRENDLINES_NTL_REEFER_LT_GRAPH_DOC],
    ['Flatbed', TRENDLINES_NTL_FLATBED_LT_GRAPH_DOC],
]);

@Injectable({
    providedIn: 'root',
})
export class IqTrendlinesGraphLtNtlStateService
    implements IIqTrendlinesGraphLtNtlStateService
{
    private _ltBaseUrl: string =
        this._getAnalyticsUrl() + inject(TL_NTL_LT_URL);
    private _db = inject(DatDbClientService);
    private DB_OPTS: { dbName: string } = {
        dbName: TRENDLINES_CACHE_DB,
    };
    private PACIFIC_TIME_ZONE: string = 'America/Los_Angeles';
    constructor(
        private _http: HttpClient,
        private _appConfig: DatiqAppConfig,
    ) {}

    private _getAnalyticsUrl(): string {
        return this._appConfig?.coreConfig?.ANALYTICS_BASE_URL ?? '';
    }

    private _isDataStale(expiryDate: string): boolean {
        const pacificExpiryDate: DateTime = DateTime.fromISO(
            expiryDate,
        ).setZone(this.PACIFIC_TIME_ZONE);
        if(isNil(pacificExpiryDate)) return true;
        const currentPacificTime: DateTime = DateTime.utc().setZone(
            this.PACIFIC_TIME_ZONE,
        );
        return currentPacificTime > pacificExpiryDate;
    }

    private _nextDay(): Date {
        //Next day at 7am
        return DateTime.utc()
            .setZone(this.PACIFIC_TIME_ZONE)
            .plus({ days: 1 })
            .set({
                hour: 7,
                minute: 0,
                second: 0,
                millisecond: 0,
            })
            .toJSDate();
    }

    async vanData(): Promise<Ratio[]> {
        return this._fetchRatios('Van');
    }
    async reeferData(): Promise<Ratio[]> {
       return this._fetchRatios('Reefer');
    }

    async flatbedData(): Promise<Ratio[]> {
        return this._fetchRatios('Flatbed');
    }

    async flatbedGraph(container: ElementRef<any>, ratios: Ratio[]) {
        FlatbedGraph.render(container, ratios);
    }
    async reeferGraph(container: ElementRef<any>, ratios: Ratio[]) {
        ReeferGraph.render(container, ratios);
    }
    async vanGraph(container: ElementRef<any>, ratios: Ratio[]) {
        VanGraph.render(container, ratios);
    }

    private async _fetchRatios(equipment: string): Promise<Ratio[]> {
        const docName: string = LT_DOC.get(equipment);
        const hasDoc: boolean = await lastValueFrom(
            this._db.hasDoc(docName, this.DB_OPTS),
        );
        
        if (!hasDoc) {
            const data: Ratio[] = await this._fetchAndCacheLtData(
                equipment,
                docName,
            );
            return data;
        }
        const cachedData: CachedLtData = (await lastValueFrom(
            this._db.getDoc(docName, this.DB_OPTS),
        )) as CachedLtData;
        if (this._isDataStale(cachedData.expiryDate)) {
            const data: Ratio[] = await this._fetchAndCacheLtData(
                equipment,
                docName,
            );
            return data;
        }
        return this._parseCachedData(cachedData.data);
    }

    private async _fetchAndCacheLtData(
        equipment: string,
        docName: string,
    ): Promise<Ratio[]> {
        const url: string = this._ltBaseUrl.replace(
            '%s',
            equipment.toUpperCase(),
        );
        const loadTruckResponse: LoadTruckResponse = await lastValueFrom(
            this._http.get<LoadTruckResponse>(url),
        );
        if (isNil(loadTruckResponse.oneMonthWeekly)) {
            return [];
        }
        const oneMonthWeekly = loadTruckResponse.oneMonthWeekly
            .reverse()
            .slice(0, 4)
            .reverse();
        const ratioMap: Ratio[] = oneMonthWeekly.map((datum) => {
            return {
                date: DateTime.fromFormat(datum.weekEndingWhen, 'yyyy-MM-dd')
                    .startOf('day')
                    .toJSDate(),
                lt: Math.round(datum.ratio * 100) / 100,
            };
        });
        this._cacheLtData(ratioMap, docName);
        return ratioMap;
    }

    private _cacheLtData(data: Ratio[], docName: string): void {
        const expiryDate = this._nextDay();
        const doc : PouchDoc = {
            data,
            expiryDate,
        };
        this._db.setDoc(docName, doc, this.DB_OPTS);
    }
    private _parseCachedData(data: Ratio[]): Ratio[] {
        return data.map((datum) => {
            return {
                date: new Date(datum.date),
                lt: datum.lt,
            };
        });
    }
}
